import { Button, Spin, Switch } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Input, Note, Page, Steps, Uniter } from "../../../helbest";
import HeaderBack from "../../../helbest/HeaderBack/HeaderBack";
import styles from "./Payment.module.css";
import {
  calculator,
  getMetadataValueByKey,
  showAllErrors,
  toFixedNumber,
} from "../../../base/utils";
import {
  useAccountUpdateMutation,
  useCheckoutAddPromoCodeMutation,
  useCheckoutBillingAddressUpdateMutation,
  useCheckoutCompleteMutation,
  useCheckoutQuery,
  useCheckoutRemovePromoCodeMutation,
  useMeQuery,
  useTransactionInitializeMutation,
} from "../../../saleor/api";
import { AddressItemTransformer } from "../../../services/objectTransformer/address";
import { _Cart_Clean } from "../../../store/actions/cart_actions";
import {
  _Clear_note_for_checkout,
  _Set_note_for_checkout,
} from "../../../store/actions/checkout_actions";
import PaymentOptions, { COD_VALUE } from "./PaymentOptions";
import { useTranslation } from "react-i18next";
import BillingAddress from "./BillingAddress";
import { useQuery } from "@apollo/client";
import { WalletQuery } from "../../../graphql/none_saleor/wallet/wallet";
import { _edit_user } from "../../../store/actions/login_actions";
import { useLangNavigate } from "../../../hooks/useLangNavigate";

const Payment = () => {
  const navigate = useLangNavigate();
  const store = useSelector((state) => state);
  const dispatch = useDispatch();
  const [TransactionInitializeMutation] = useTransactionInitializeMutation();
  const [value, setValue] = useState(null);
  const [loading, setLoading] = useState(false);

  const { t, i18n } = useTranslation();

  const syncCartDataWithCheckoutTotal = (checkout) => {
    const checkoutGross = checkout?.totalPrice?.gross?.amount;
    if (checkoutGross === null || checkoutGross === undefined) return;

    const cartProducts = [
      ...new Set(store.Cart.map(({ product }) => product)),
    ];

    let totalAmount = 0;
    let taxAmount = 0;
    let payableWithTaxFromCart = 0;
    let itemDiscountAmount = 0;

    cartProducts.forEach((x) => {
      totalAmount += Number(x.quantity) * Number(x.price);
      taxAmount += Number(x.quantity) * Number(x.tax);
      payableWithTaxFromCart +=
        Number(x.quantity) * Number(x.payable_amount) +
        Number(x.quantity) * Number(x.tax);
      itemDiscountAmount += Number(x.quantity) * Number(x.discount_amount);
    });

    // If a promo/voucher is applied on checkout, checkoutGross may be less than
    // locally computed payableWithTaxFromCart. We fold the delta into discount_amount
    // so summary lines stay consistent after navigating back/forth.
    const promoDiscountDelta = Math.max(
      0,
      Number(payableWithTaxFromCart) - Number(checkoutGross)
    );

    const nextPayable = toFixedNumber(checkoutGross);
    const nextTotal = toFixedNumber(totalAmount);
    const nextTax = toFixedNumber(taxAmount);
    const nextDiscount = toFixedNumber(
      Number(itemDiscountAmount) + Number(promoDiscountDelta)
    );

    const currentPayable = Number(store.Login?.cart_data?.payable_amount);
    const shouldSync =
      !Number.isFinite(currentPayable) ||
      Math.abs(currentPayable - Number(checkoutGross)) > 0.001;

    if (!shouldSync) return;

    calculator({
      ...store.Login.cart_data,
      total_amount: nextTotal,
      tax_amount: nextTax,
      discount_amount: nextDiscount,
      payable_amount: nextPayable,
    });
  };

  const { data: checkoutData, refetch: checkoutRefetch } = useCheckoutQuery({
    variables: {
      id: store.Login.checkoutId,
    },
    cacheTime: 0,
    staleTime: 0,
    skip: !store.Login.token,
    onCompleted(response) {
      setChangeBillingAddress(false);

      const walletGiftCard = response.checkout.giftCards.find(
        (card) =>
          card.metadata.find((meta) => meta.key === "tag_name")?.value ===
          "wallet"
      );

      if (walletGiftCard) {
        inputOnChange("useCredit", true);
      }

      const voucherCode = response?.checkout?.voucherCode;
      setPromoCode(voucherCode || "");
      setPromoCodeAdded(!!voucherCode);

      setSelectedBillingAddress(
        AddressItemTransformer(response.checkout.billingAddress)
      );

      syncCartDataWithCheckoutTotal(response?.checkout);
    },
    onError: (error) => {
      Note.error(error.message);
    },
  });

  const [userDetails, setUserDetails] = useState({
    first_name: store.Login?.first_name || "",
    last_name: store.Login?.last_name || "",
    useCredit: false,
    vat_number: "",
  });

  const [walletLoading, setWalletLoading] = useState(false);
  const [useWallet, setUseWallet] = useState(false);
  const [showAddressPopup, setShowAddressPopup] = useState(false);

  const [changeBillingAddress, setChangeBillingAddress] = useState(true);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(true);
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeAdded, setPromoCodeAdded] = useState(false);
  const checkoutInfo = checkoutData?.checkout ?? {};
  const payableAmount = store.Login.cart_data.payable_amount;

  const { data: walletCredit, refetch: getWalletDetails } = useQuery(
    WalletQuery,
    {
      context: {
        clientName: "wallet",
      },
      onError: (error) => {
        Note.error(error.message);
      },
      variables: {
        channel: store.Login.channel,
      },
    }
  );

  // Wallet is displayed and calculated with 3 decimal places.
  // For example, 0.00030 is treated as 0.
  const walletBalance = Number(
    Number(walletCredit?.wallet?.balance || 0).toFixed(3)
  );

  useEffect(() => {
    getWalletDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUseWallet(walletBalance > 0);
  }, [walletBalance]);

  const [checkoutBillingAddressUpdateMutation] =
    useCheckoutBillingAddressUpdateMutation({
      onCompleted(response) {
        checkoutRefetch();
        if (!response.checkoutBillingAddressUpdate.errors.length) {
          setShowAddressPopup(false);
        } else {
          showAllErrors(response.checkoutBillingAddressUpdate.errors);
        }
      },
    });

  const [checkoutCompleteMutation] = useCheckoutCompleteMutation({
    variables: {
      id: store.Login.checkoutId,
      metadata: store.Checkout.note
        ? [{ key: "Note", value: store.Checkout.note }]
        : [],
    },
    onCompleted: (response) => {
      if (response.checkoutComplete.errors.length) {
        showAllErrors(response.checkoutComplete.errors);
      } else {
        navigate(
          `/payment/done?is_wallet_charge=true&orderNumber=${response.checkoutComplete.order.number}`
        );
        dispatch(_Cart_Clean());
        dispatch(_Clear_note_for_checkout());
        calculator();
      }
    },
  });

  const { data: profileData } = useMeQuery({
    variables: {},
  });

  useEffect(() => {
    if (profileData?.me) {
      const vat = getMetadataValueByKey(
        profileData.me.metadata,
        "vat_number"
      );
      inputOnChange("vat_number", vat);
    } else {
      inputOnChange("vat_number", "");
    }
  }, [profileData]);

  const inputOnChange = (label, value) => {
    setUserDetails((state) => ({
      ...state,
      [label]: value,
    }));
  };

  const [selectedAddress, setSelectedAddress] = useState("");

  const onWalletChange = (active) => {
    setUseWallet(active);
    setWalletLoading(true);

    setTimeout(() => {
      setWalletLoading(false);
    }, 1000);
  };

  const [checkoutAddPromoCodeMutation, { loading: discountLoading }] =
    useCheckoutAddPromoCodeMutation({
      variables: {
        id: store.Login.checkoutId,
        promoCode,
      },
      onCompleted(response) {
        if (!response.checkoutAddPromoCode.errors.length) {
          Note.success("promoCode added successfully");
          setPromoCodeAdded(true);
          syncCartDataWithCheckoutTotal(
            response?.checkoutAddPromoCode?.checkout
          );
          checkoutRefetch();
        } else {
          showAllErrors(response.checkoutAddPromoCode.errors);
        }
      },
    });

  const [
    checkoutRemovePromoCodeMutation,
    { loading: discountRemoveLoading },
  ] = useCheckoutRemovePromoCodeMutation({
    variables: {
      id: store.Login.checkoutId,
      promoCode,
    },
    onCompleted(response) {
      if (!response.checkoutRemovePromoCode.errors.length) {
        Note.success("promoCode removed successfully");
        syncCartDataWithCheckoutTotal(
          response?.checkoutRemovePromoCode?.checkout
        );
        checkoutRefetch();
      } else {
        showAllErrors(response.checkoutRemovePromoCode.errors);
      }
    },
  });

  const [accountUpdate] = useAccountUpdateMutation({
    onCompleted: (response) => {
      showAllErrors(response.accountUpdate.errors);
    },
  });

  function calculatePayableAmount(payableAmount, walletBalance) {
    if (useWallet) {
      if (Number(walletBalance) >= payableAmount) {
        return 0;
      }

      return Number(payableAmount - walletBalance);
    }

    return Number(payableAmount);
  }

  // Shared derived values — used by checkoutDisable(), the summary display,
  // PaymentOptions, and handlePaymentNavigate, so they all agree on the same numbers.
  const payable = calculatePayableAmount(payableAmount, walletBalance);

  const rawGatewayData = store.Login?.paymentOptions?.[0]?.data;
  const gateways = Array.isArray(rawGatewayData) ? rawGatewayData : [];
  const hasGateways = gateways.length > 0;

  const handlePaymentNavigate = () => {
    const firstName = userDetails?.first_name?.trim();
    const lastName = userDetails?.last_name?.trim();
    const vat_number = userDetails?.vat_number?.trim();

    if (!firstName && !lastName) {
      Note.error(t("profile.errors.first_name_and_last_name_required"));
    } else if (!firstName) {
      Note.error(t("profile.errors.first_name_required"));
    } else if (!lastName) {
      Note.error(t("profile.errors.last_name_required"));
    } else if (!vat_number) {
      Note.error(t("profile.errors.vat_number_required"));
    } else if (!value && Number(payable) !== 0) {
      // Payment is required (payable !== 0) and user hasn't picked a gateway.
      Note.error(t("cart.options_required"));
      return;
    } else {
      setLoading(true);
      accountUpdate({
        variables: {
          input: {
            firstName: userDetails.first_name,
            lastName: userDetails.last_name,
            metadata: [
              {
                key: "vat_number",
                value: vat_number,
              },
            ],
          },
        },
      })
        .then(() => {
          dispatch(
            _edit_user({
              first_name: userDetails.first_name,
              last_name: userDetails.last_name,
            })
          );

          if (value === COD_VALUE) {
            const remainder = calculatePayableAmount(
              payableAmount,
              walletBalance
            );

            const walletCovered =
              useWallet && walletBalance > 0;

            // COD-specific metadata: tag the order's payment method as
            // "cash_on_delivery" in addition to any user note. Only built
            // and used within this branch so gateway/wallet-only completions
            // elsewhere are unaffected.
            const codMetadata = [
              ...(store.Checkout.note
                ? [{ key: "Note", value: store.Checkout.note }]
                : []),
              { key: "payment_method", value: "cash_delivery" },
            ];

            if (!walletCovered) {
              // No wallet credit applied at all — full amount deferred to COD.
              setLoading(false);
              checkoutCompleteMutation({
                variables: {
                  id: store.Login.checkoutId,
                  metadata: codMetadata,
                },
              });
              return;
            }

            // Wallet covers all or part of the total. Charge only the
            // wallet-covered portion now; any remainder stays unpaid and is
            // deferred to COD (no second gateway TransactionInitialize fires).
            const walletChargeAmount =
              remainder === 0
                ? payableAmount
                : Math.round((payableAmount - remainder) * 1000) / 1000;

            TransactionInitializeMutation({
              variables: {
                id: store.Login.checkoutId,
                paymentGateway: {
                  id: "saleor-payment-gateways-center",
                  data: {
                    gateway_id: "WALLET",
                    callback_host: window.location.origin,
                  },
                },
                amount: walletChargeAmount,
              },
              onCompleted: (response) => {
                setLoading(false);
                if (response.transactionInitialize.data.success) {
                  checkoutCompleteMutation({
                    variables: {
                      id: store.Login.checkoutId,
                      metadata: codMetadata,
                    },
                  });
                } else {
                  Note.error(response.transactionInitialize.data.message);
                  setValue(null);
                }
              },
            });

            return;
          }

          if (
            calculatePayableAmount(
              payableAmount,
              walletBalance
            ) !== 0 &&
            useWallet
          ) {
            TransactionInitializeMutation({
              variables: {
                id: store.Login.checkoutId,
                paymentGateway: {
                  id: "saleor-payment-gateways-center",
                  data: {
                    gateway_id: "WALLET",
                    callback_host: window.location.origin,
                  },
                },
                amount:
                  Math.round(
                    (
                      payableAmount -
                      calculatePayableAmount(
                        payableAmount,
                        walletBalance
                      )
                    ) *
                      1000
                  ) / 1000,
              },
              onCompleted: (response) => {
                if (response.transactionInitialize.data.success) {
                  TransactionInitializeMutation({
                    variables: {
                      id: store.Login.checkoutId,
                      paymentGateway: {
                        id: "saleor-payment-gateways-center",
                        data: {
                          gateway_id: value,
                          callback_host: window.location.origin,
                        },
                      },
                    },
                    onCompleted: (response) => {
                      setLoading(false);
                      if (response.transactionInitialize.data.success) {
                        window.location.href =
                          response.transactionInitialize.data.url;
                      } else {
                        Note.error(
                          response.transactionInitialize.data.message
                        );
                        setValue(null);
                      }
                    },
                  });
                } else {
                  Note.error(response.transactionInitialize.data.message);
                  setValue(null);
                }
              },
            });
          } else if (
            calculatePayableAmount(
              payableAmount,
              walletBalance
            ) !== 0 &&
            !useWallet
          ) {
            TransactionInitializeMutation({
              variables: {
                id: store.Login.checkoutId,
                paymentGateway: {
                  id: "saleor-payment-gateways-center",
                  data: {
                    gateway_id: value,
                    callback_host: window.location.origin,
                  },
                },
              },
              onCompleted: (response) => {
                setLoading(false);
                if (response.transactionInitialize.data.success) {
                  window.location.href =
                    response.transactionInitialize.data.url;
                } else {
                  Note.error(response.transactionInitialize.data.message);
                  setValue(null);
                }
              },
            });
          } else if (
            calculatePayableAmount(
              payableAmount,
              walletBalance
            ) === 0 &&
            useWallet &&
            Number(payableAmount) !== 0
          ) {
            TransactionInitializeMutation({
              variables: {
                id: store.Login.checkoutId,
                paymentGateway: {
                  id: "saleor-payment-gateways-center",
                  data: {
                    gateway_id: "WALLET",
                    callback_host: window.location.origin,
                  },
                },
                amount: payableAmount,
              },
              onCompleted: (response) => {
                setLoading(false);
                if (response.transactionInitialize.data.success) {
                  checkoutCompleteMutation();
                } else {
                  Note.error(response.transactionInitialize.data.message);
                  setValue(null);
                }
              },
            });
          } else if (
            calculatePayableAmount(
              payableAmount,
              walletBalance
            ) === 0
          ) {
            setLoading(false);
            checkoutCompleteMutation();
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    if (value === "QPAY") {
      handlePaymentNavigate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    // When user navigates back to cart, modifies lines, then comes back,
    // checkout can already have a voucher applied but local cart_data may be stale.
    // Keep summary derived from checkout totals to prevent inconsistent prices.
    if (checkoutData?.checkout) {
      syncCartDataWithCheckoutTotal(checkoutData.checkout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkoutData?.checkout?.id,
    checkoutData?.checkout?.quantity,
    checkoutData?.checkout?.voucherCode,
    checkoutData?.checkout?.totalPrice?.gross?.amount,
  ]);

  const checkoutDisable = () => {
    if (loading) {
      return true;
    } else if (changeBillingAddress && selectedAddress === "") {
      return true;
    } else return false;
  };

  const footer = (
    <div className="page-fixed-footer-container">
      <Button
        disabled={checkoutDisable()}
        loading={loading}
        size="large"
        type="primary"
        style={{
          width: "100%",
        }}
        onClick={() => {
          handlePaymentNavigate();
        }}
      >
        {t("payment.complete_order")}
      </Button>
    </div>
  );

  return (
    <React.Fragment>
      <Page
        className="page-payment"
        title="Payment"
        address={"/cart/"}
        showFooter
        footer={footer}
        customHeader={<HeaderBack title={t("cart.payment_header")} />}
        extra_header={<Steps step={3} hasShadow />}
        headerFix
        logo
        back
      >
        <section className="payment-page-body">
          <Box className="payment-data-wrap-up">
            <div className={styles.BoxTitle}>{t("cart.order_summary")}</div>

            <div className="ws-100 mb-1">
              <span>{t("summery.price")}</span>
              <Uniter>
                {toFixedNumber(store.Login?.cart_data?.total_amount) || ""}
              </Uniter>
            </div>

            {store.Login?.cart_data?.discount_amount >= 0 && (
              <div className="ws-100 mb-1">
                <span>{t("summery.discount")}</span>
                <Uniter>
                  {-toFixedNumber(
                    store.Login?.cart_data?.discount_amount
                  ) || 0}
                </Uniter>
              </div>
            )}

            {checkoutInfo?.totalPrice?.tax &&
              checkoutInfo?.totalPrice?.tax?.amount >= 0 && (
                <div className="ws-100 mb-1">
                  <span>{t("summary.tax")}</span>
                  <span>
                    <Uniter>
                      {toFixedNumber(
                        store.Login?.cart_data?.tax_amount
                      ) || ""}
                    </Uniter>
                  </span>
                </div>
              )}

            <div className="ws-100">
              <div className="flex wallet-text-container">
                <span className="flex items-center gap-2">
                  {t("common.wallet")}
                </span>

                {walletCredit?.wallet ? (
                  <Uniter
                    childSize={12}
                    unitSize={8}
                    className={
                      useWallet
                        ? "green-text"
                        : "low-opacity-text"
                    }
                    isBold
                    noTax
                  >
                    <span
                      className={
                        useWallet
                          ? "blue-text"
                          : "low-opacity-text"
                      }
                    >
                      {Number(toFixedNumber(walletBalance))}
                    </span>
                  </Uniter>
                ) : null}
              </div>

              <div>
                <Switch
                  loading={walletLoading}
                  onChange={(checked) => onWalletChange(checked)}
                  disabled={
                    !walletCredit?.wallet ||
                    walletBalance === 0
                  }
                  value={useWallet}
                />
              </div>
            </div>

            <div className="payment-data-line-break"></div>

            <div className="ws-100">
              <span>{t("summery.payable")}</span>

              {walletLoading ? (
                <Spin size="small" />
              ) : (
                <Uniter
                  isBold
                  className={styles.PayableAmountContainer}
                >
                  {toFixedNumber(payable)}
                </Uniter>
              )}
            </div>
          </Box>

          <div className={styles.DiscountContainer}>
            <Input
              dataHandler={(value) =>
                setPromoCode(value?.toLowerCase() || "")
              }
              data={promoCode}
              className={`${styles.Input} ${
                i18n.dir() === "rtl" ? "rtl-placeholder" : ""
              }`}
              allowClear={false}
              maxLength={15}
              style={{ marginTop: "0" }}
              placeholder={t("cart.discount_placeholder")}
              disabled={
                discountLoading ||
                discountRemoveLoading ||
                promoCodeAdded
              }
            />

            {promoCodeAdded ? (
              <Button
                onClick={() => {
                  if (!promoCode) {
                    return Note.error(
                      "Failed to retrieve current promo code"
                    );
                  }

                  setPromoCodeAdded(false);
                  checkoutRemovePromoCodeMutation();
                }}
                style={{
                  width: "100%",
                  maxWidth: "150px",
                }}
                type="primary"
                loading={discountLoading}
              >
                {t("common.remove")}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  if (!promoCode) {
                    return Note.error("Enter your promoCode");
                  }

                  checkoutAddPromoCodeMutation();
                }}
                style={{
                  width: "100%",
                  maxWidth: "150px",
                }}
                type="primary"
                loading={discountLoading}
              >
                {t("common.apply")}
              </Button>
            )}
          </div>

          <Box className="payment-data-wrap-up mt-2">
            <div className={styles.BoxTitle}>
              {t("cart.user_information")}
            </div>

            <Input
              dataHandler={(value) =>
                inputOnChange("vat_number", value)
              }
              className={`${styles.Input} ${styles.Infos} ${
                i18n.dir() === "rtl" ? "rtl-placeholder" : ""
              }`}
              allowClear={false}
              maxLength={16}
              data={userDetails?.vat_number || ""}
              style={{ marginTop: "0" }}
              placeholder={t("common.vat_number")}
              pattern=".*"
              inputMode="text"
            />

            <Input
              dataHandler={(value) =>
                inputOnChange("first_name", value)
              }
              className={`${styles.Input} ${styles.Infos} ${
                i18n.dir() === "rtl" ? "rtl-placeholder" : ""
              }`}
              allowClear={false}
              maxLength={16}
              disabled={!!store.Login?.first_name}
              data={userDetails?.first_name || ""}
              style={{ marginTop: "0" }}
              placeholder={t("common.first_name")}
              pattern=".*"
              inputMode="text"
            />

            <Input
              dataHandler={(value) =>
                inputOnChange("last_name", value)
              }
              className={`${styles.Input} ${styles.Infos} ${
                i18n.dir() === "rtl" ? "rtl-placeholder" : ""
              } `}
              allowClear={false}
              disabled={!!store.Login?.last_name}
              data={userDetails?.last_name || ""}
              maxLength={16}
              style={{ marginTop: "0" }}
              placeholder={t("common.last_name")}
              pattern=".*"
              inputMode="text"
            />

            <Input
              dataHandler={(value) =>
                dispatch(_Set_note_for_checkout(value))
              }
              className={`${styles.Input} ${styles.Infos} ${
                i18n.dir() === "rtl" ? "rtl-placeholder" : ""
              }`}
              allowClear={false}
              // data={store.Checkout?.note || ""}
              style={{ marginTop: "0" }}
              placeholder={t("common.note")}
              pattern=".*"
              inputMode="text"
            />

            <div className="payment-data-line-break" />

            <PaymentOptions
              value={value}
              setValue={setValue}
              payable={payable}
            />

            <div className="payment-data-line-break" />

            <BillingAddress
              checkoutData={checkoutData}
              changeBillingAddress={changeBillingAddress}
              setChangeBillingAddress={setChangeBillingAddress}
              checkoutBillingAddressUpdateMutation={
                checkoutBillingAddressUpdateMutation
              }
              selectedAddress={selectedAddress}
              selectedBillingAddress={selectedBillingAddress}
              setShowAddressPopup={setShowAddressPopup}
              setSelectedAddress={setSelectedAddress}
              showAddressPopup={showAddressPopup}
            />
          </Box>
        </section>
      </Page>
    </React.Fragment>
  );
};

export default Payment;